/**
 * Register Page
 * Registration form with password strength indicator
 */

import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@platform/ui";
import { Input } from "@platform/ui";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@platform/ui";

export function RegisterPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<
    "weak" | "medium" | "strong"
  >("weak");
  const { signUp } = useAuth();

  const calculatePasswordStrength = (
    password: string,
  ): "weak" | "medium" | "strong" => {
    if (password.length < 8) return "weak";
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const strength = [hasUpper, hasLower, hasNumber, hasSpecial].filter(
      Boolean,
    ).length;
    if (strength >= 3 && password.length >= 12) return "strong";
    if (strength >= 2 && password.length >= 8) return "medium";
    return "weak";
  };

  const handlePasswordChange = (password: string) => {
    setFormData((prev) => ({ ...prev, password }));
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.fullName.trim()) {
      setError("Full name is required");
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!formData.acceptTerms) {
      setError("You must accept the Terms of Service");
      return;
    }

    setLoading(true);
    try {
      const result = await signUp(
        formData.email,
        formData.password,
        formData.fullName,
      );
      if (result.success) {
        setSuccess(true);
        setSuccessMessage(result.message);
      } else {
        setError(result.message);
      }
    } catch (_err) {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-center">Check your email</CardTitle>
          <CardDescription className="text-center">
            We've sent you a verification link
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div
              className="rounded-md bg-[var(--color-success-50)] border border-[var(--color-success-500)] p-4"
              role="status"
            >
              <p className="text-sm text-[var(--color-text-primary)]">
                {successMessage}
              </p>
            </div>
            <p className="text-sm text-[var(--color-text-secondary)] text-center">
              Please check your inbox at <strong>{formData.email}</strong> and
              click the verification link.
            </p>
            <Link to="/login" className="block">
              <Button variant="primary" className="w-full">
                Go to Sign In
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center">Create your account</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className="rounded-md bg-[var(--color-error-50)] border border-[var(--color-error-500)] p-4"
              role="alert"
            >
              <p className="text-sm text-[var(--color-error-500)]">{error}</p>
            </div>
          )}
          <Input
            id="fullName"
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData.fullName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, fullName: e.target.value }))
            }
            disabled={loading}
            required
            aria-label="Full name"
          />
          <Input
            id="email"
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            disabled={loading}
            required
            aria-label="Email address"
          />
          <div>
            <Input
              id="password"
              label="Password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              disabled={loading}
              required
              helperText="At least 8 characters with letters, numbers, and symbols"
              aria-label="Password"
            />
            {formData.password && (
              <div className="mt-2">
                <div className="flex gap-2">
                  <div
                    className={`h-1.5 flex-1 rounded ${passwordStrength === "weak" ? "bg-[var(--color-error-500)]" : passwordStrength === "medium" ? "bg-[var(--color-warning-500)]" : "bg-[var(--color-success-500)]"}`}
                  />
                  <div
                    className={`h-1.5 flex-1 rounded ${passwordStrength === "medium" || passwordStrength === "strong" ? (passwordStrength === "medium" ? "bg-[var(--color-warning-500)]" : "bg-[var(--color-success-500)]") : "bg-[var(--color-secondary-200)]"}`}
                  />
                  <div
                    className={`h-1.5 flex-1 rounded ${passwordStrength === "strong" ? "bg-[var(--color-success-500)]" : "bg-[var(--color-secondary-200)]"}`}
                  />
                </div>
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                  Strength:{" "}
                  <span className="capitalize">{passwordStrength}</span>
                </p>
              </div>
            )}
          </div>
          <Input
            id="confirmPassword"
            label="Confirm Password"
            type="password"
            placeholder="••••••••"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
              }))
            }
            disabled={loading}
            required
            error={
              formData.confirmPassword &&
              formData.password !== formData.confirmPassword
                ? "Passwords do not match"
                : undefined
            }
            aria-label="Confirm password"
          />
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    acceptTerms: e.target.checked,
                  }))
                }
                disabled={loading}
                className="h-4 w-4 rounded border-[var(--color-border-default)] bg-[var(--color-bg-tertiary)] text-[var(--color-primary-500)] focus:ring-2 focus:ring-[var(--color-primary-500)] focus:ring-offset-2"
                aria-label="Accept terms of service"
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor="terms"
                className="text-[var(--color-text-secondary)]"
              >
                I agree to the{" "}
                <Link
                  to="/legal/terms"
                  className="text-[var(--color-primary-500)] hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/legal/privacy"
                  className="text-[var(--color-primary-500)] hover:underline"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full"
            aria-busy={loading}
          >
            {loading ? "Creating account..." : "Create account"}
          </Button>
          <p className="text-center text-sm text-[var(--color-text-secondary)]">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-[var(--color-primary-500)] hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
